import { FC, PropsWithChildren, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as z from "zod";

import { Button } from "@/components/_atoms/button";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/_atoms/form";
import { Input } from "@/components/_atoms/input";
import { LoadingDots } from "@/components/_molecules/loading-dots/loading-dots";
import { zodResolver } from "@hookform/resolvers/zod";

import { resetPasswordWithToken } from "../services/auth.service";

export interface PasswordResetFormProps extends PropsWithChildren {
  token: string;
}

export const PasswordResetForm: FC<PasswordResetFormProps> = ({ token }) => {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation("auth");

  const formSchema = useMemo(
    () =>
      z
        .object({
          newpassword: z.string().min(8, {
            message: t("passwordReset.passwordMinLength"),
          }),
          confirmation: z.string(),
        })
        .refine((data) => data.newpassword === data.confirmation, {
          message: t("passwordReset.passwordMismatch"),
          path: ["confirmation"],
        }),
    [t],
  );

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      newpassword: "",
      confirmation: "",
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setLoading(true);

    try {
      await resetPasswordWithToken(token, values.newpassword);
      window.location.replace(new URL(`${window.location.origin}/`));
    } catch (e) {
      form.setError("confirmation", {
        message: t("passwordReset.genericError"),
      });
    } finally {
      setLoading(false);
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 px-8 py-8">
        <FormField
          control={form.control}
          name="newpassword"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("passwordReset.newPassword")}</FormLabel>
              <FormDescription>{t("passwordReset.newPasswordDescription")}</FormDescription>
              <FormControl>
                <Input type="password" {...field} autoComplete="new-password" aria-label={t("passwordReset.newPassword")} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="confirmation"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("passwordReset.confirmPasswordLabel")}</FormLabel>
              <FormDescription>{t("passwordReset.confirmPasswordDescription")}</FormDescription>
              <FormControl>
                <Input type="password" {...field} autoComplete="new-password" aria-label={t("passwordReset.confirmPasswordLabel")} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex flex-row items-center justify-start">
          <Button
            type="submit"
            disabled={loading}
            className={`${
              loading ? "cursor-not-allowed bg-muted" : "bg-primary text-primary-foreground hover:bg-primary/90"
            } flex h-10 w-32 items-center justify-center rounded-md border text-sm transition-all  `}
          >
            {loading ? <LoadingDots color="#808080" /> : <p>{t("passwordReset.submit")}</p>}
          </Button>
        </div>
      </form>
    </Form>
  );
};
